import React, { useState, useEffect } from 'react';
import { Wheel } from 'react-custom-roulette';
import {
    Button,
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Zoom,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CelebrationIcon from '@mui/icons-material/Celebration';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// Animations
const pulse = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
`;

const confetti = keyframes`
    0% { transform: translateY(-100%) rotate(0deg); opacity: 1; }
    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
`;

const bounce = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
`;

const float = keyframes`
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-15px) rotate(5deg); }
`;

// Styled Components
const WheelContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
    padding: 40,
    background: '#fff',
    borderRadius: 32,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
            'radial-gradient(circle at 50% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 50%)',
        pointerEvents: 'none',
    },
});

const SpinButton = styled(Button)(({ theme }) => ({
    borderRadius: 50,
    fontSize: 16,
    fontWeight: 700,
    background: theme.palette.primary.main,
    color: '#fff',
    textTransform: 'none',
    boxShadow: `0 8px 32px ${theme.palette.primary.main}66`,
    transition: 'all 0.3s ease',
    '&:hover': {
        background: theme.palette.primary.dark,
        transform: 'translateY(-3px)',
        boxShadow: `0 12px 40px ${theme.palette.primary.main}80`,
    },
    '&:disabled': {
        background: 'linear-gradient(135deg, #374151 0%, #4B5563 100%)',
        color: '#9CA3AF',
        boxShadow: 'none',
    },
    '&:not(:disabled)': {
        animation: `${pulse} 2s ease-in-out infinite`,
    },
}));

const Title = styled(Typography)(({ theme }) => ({
    fontSize: 36,
    fontWeight: 800,
    textAlign: 'center',
    color: theme.palette.primary.main,
}));

const PrizeDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 28,
        background: '#fff',
        border: `2px solid ${theme.palette.primary.main}4D`,
        overflow: 'visible',
        maxWidth: 450,
    },
}));

const PrizeContent = styled(DialogContent)({
    padding: 48,
    textAlign: 'center',
    position: 'relative',
    overflow: 'visible',
});

const PrizeIcon = styled(Box)(({ theme }) => ({
    width: 110,
    height: 110,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '-100px auto 28px',
    boxShadow: `0 8px 32px ${theme.palette.primary.main}80`,
    animation: `${bounce} 1s ease-in-out infinite`,
}));

const Confetti = styled(Box)<{ delay: number; left: number }>(
    ({ delay, left }) => ({
        position: 'absolute',
        top: 0,
        left: `${left}%`,
        width: 10,
        height: 10,
        borderRadius: 2,
        animation: `${confetti} 3s linear infinite`,
        animationDelay: `${delay}s`,
    })
);

const FloatingIcon = styled(Box)<{ top: number; left: number; delay: number }>(
    ({ top, left, delay }) => ({
        position: 'absolute',
        top: `${top}%`,
        left: `${left}%`,
        animation: `${float} 3s ease-in-out infinite`,
        animationDelay: `${delay}s`,
        opacity: 0.6,
    })
);

// Interface
export interface IWheelReward {
    id: number;
    name: string;
    image?: string;
    quantity?: number;
}

interface IProps {
    rewards: IWheelReward[];
    winningReward: IWheelReward | null;
    onSpin: () => Promise<void>;
    onClose?: () => void;
    disabled?: boolean;
    isWinning?: boolean; // Add flag to indicate if user won
    resultMessage?: string; // Message from backend
}

const WheelOfName: React.FC<IProps> = ({
    rewards = [],
    winningReward,
    onSpin,
    onClose,
    disabled = false,
    isWinning = true,
    resultMessage = '',
}) => {
    const [mustSpin, setMustSpin] = useState(false);
    const [prizeNumber, setPrizeNumber] = useState(0);
    const [showResult, setShowResult] = useState(false);
    const [pendingSpin, setPendingSpin] = useState(false);
    const [hasSpun, setHasSpun] = useState(false);
    const [spinResult, setSpinResult] = useState<{
        isWinning: boolean;
        reward: IWheelReward | null;
    } | null>(null);
    console.log(winningReward);

    // Background colors for wheel segments
    const backgroundColors = [
        '#6366F1', // Indigo
        '#EC4899', // Pink
        '#8B5CF6', // Purple
        '#14B8A6', // Teal
        '#F59E0B', // Amber
        '#EF4444', // Red
        '#06B6D4', // Cyan
        '#10B981', // Emerald
    ];

    // Map rewards to wheel data format
    const data = rewards.map((r) => ({
        option: r.name,
        id: r.id,
    }));

    // Handle spin when API returns result
    useEffect(() => {
        if (pendingSpin) {
            let prizeIndex = 0;

            if (isWinning && winningReward) {
                // User won - spin to the winning reward
                prizeIndex = rewards.findIndex(
                    (r) => r.id === winningReward.id
                );
                if (prizeIndex === -1) prizeIndex = 0;
                setSpinResult({ isWinning: true, reward: winningReward });
            } else if (!isWinning && winningReward) {
                // User didn't win - spin to "Chúc bạn may mắn lần sau"
                prizeIndex = rewards.findIndex(
                    (r) => r.id === winningReward.id
                );
                if (prizeIndex === -1) prizeIndex = 0;
                setSpinResult({ isWinning: false, reward: winningReward });
            } else {
                // Waiting for API response
                return;
            }

            setPrizeNumber(prizeIndex);
            setMustSpin(true);
            setPendingSpin(false);
        }
    }, [winningReward, pendingSpin, rewards, isWinning]);

    const startSpin = async () => {
        if (mustSpin || disabled || rewards.length === 0 || hasSpun) return;

        setPendingSpin(true);
        setShowResult(false);

        try {
            // Call API to get winning prize
            await onSpin();
            setHasSpun(true);
        } catch (error) {
            console.error('Spin error:', error);
            setPendingSpin(false);
        }
    };

    const handleStopSpinning = () => {
        setMustSpin(false);
        // Always show result dialog
        setShowResult(true);
    };

    const handleCloseResult = () => {
        setShowResult(false);
        onClose?.();
    };

    // Confetti colors
    const confettiColors = [
        '#6366F1',
        '#EC4899',
        '#8B5CF6',
        '#14B8A6',
        '#F59E0B',
        '#EF4444',
    ];

    if (rewards.length === 0) {
        return (
            <WheelContainer>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Không có phần thưởng
                </Typography>
            </WheelContainer>
        );
    }

    return (
        <>
            <WheelContainer>
                {/* Floating decorations */}
                <FloatingIcon top={5} left={10} delay={0}>
                    <AutoAwesomeIcon sx={{ color: '#6366F1', fontSize: 28 }} />
                </FloatingIcon>
                <FloatingIcon top={15} left={85} delay={0.5}>
                    <AutoAwesomeIcon sx={{ color: '#EC4899', fontSize: 22 }} />
                </FloatingIcon>
                <FloatingIcon top={80} left={5} delay={1}>
                    <AutoAwesomeIcon sx={{ color: '#8B5CF6', fontSize: 20 }} />
                </FloatingIcon>
                <FloatingIcon top={75} left={90} delay={1.5}>
                    <AutoAwesomeIcon sx={{ color: '#14B8A6', fontSize: 24 }} />
                </FloatingIcon>

                <Title>Vòng Quay May Mắn</Title>

                <Typography
                    sx={{
                        color: 'rgba(0,0,0,0.7)',
                        fontSize: 16,
                        textAlign: 'center',
                        mb: 1,
                    }}
                >
                    Quay và nhận phần thưởng hấp dẫn!
                </Typography>

                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 2,
                    }}
                >
                    <Wheel
                        mustStartSpinning={mustSpin}
                        prizeNumber={prizeNumber}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        data={data as any}
                        backgroundColors={backgroundColors}
                        textColors={['#fff']}
                        outerBorderColor="rgba(255, 255, 255, 0.3)"
                        outerBorderWidth={3}
                        radiusLineColor="rgba(255, 255, 255, 0.2)"
                        radiusLineWidth={2}
                        fontSize={rewards.length > 6 ? 14 : 16}
                        textDistance={60}
                        onStopSpinning={handleStopSpinning}
                        spinDuration={0.5}
                    />
                </Box>

                <SpinButton
                    variant="contained"
                    size="medium"
                    disabled={mustSpin || disabled || hasSpun}
                    onClick={startSpin}
                >
                    {mustSpin ? '🎁 Đang quay...' : '🚀 Quay Ngay!'}
                </SpinButton>
            </WheelContainer>

            {/* Prize Result Dialog */}
            <PrizeDialog
                open={showResult}
                onClose={handleCloseResult}
                TransitionComponent={Zoom}
                transitionDuration={500}
            >
                {/* Confetti Effect */}
                {showResult && (
                    <>
                        {[...Array(20)].map((_, i) => (
                            <Confetti
                                key={i}
                                delay={i * 0.15}
                                left={Math.random() * 100}
                                sx={{
                                    backgroundColor:
                                        confettiColors[
                                            i % confettiColors.length
                                        ],
                                    transform: `rotate(${Math.random() * 360}deg)`,
                                }}
                            />
                        ))}
                    </>
                )}

                <IconButton
                    onClick={handleCloseResult}
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 100,
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                            color: '#fff',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <PrizeContent>
                    <PrizeIcon>
                        <CelebrationIcon sx={{ fontSize: 55, color: '#fff' }} />
                    </PrizeIcon>

                    {isWinning ? (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontSize: 32,
                                    mb: 2,
                                    gap: 1,
                                }}
                            >
                                🎉
                                <Typography
                                    sx={{
                                        fontSize: 32,
                                        fontWeight: 800,
                                        color: 'primary.main',
                                    }}
                                >
                                    Chúc Mừng!
                                </Typography>
                                🎉
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: 16,
                                    color: 'rgba(0,0,0,0.8)',
                                    mb: 3,
                                }}
                            >
                                {resultMessage || 'Bạn đã trúng thưởng'}
                            </Typography>

                            <Box
                                sx={{
                                    backgroundColor: 'primary.main',
                                    borderRadius: 4,
                                    padding: '24px 36px',
                                    mb: 3,
                                }}
                            >
                                {spinResult?.reward?.image && (
                                    <Box
                                        component="img"
                                        src={spinResult.reward.image}
                                        alt={spinResult.reward.name}
                                        sx={{
                                            width: [250],
                                            objectFit: 'contain',
                                            mb: 2,
                                            borderRadius: 2,
                                        }}
                                    />
                                )}
                                <Typography
                                    sx={{
                                        fontSize: 26,
                                        fontWeight: 700,
                                        color: '#fff',
                                    }}
                                >
                                    🎁 {spinResult?.reward?.name}
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: 'rgba(0,0,0,0.6)',
                                    mb: 3,
                                }}
                            >
                                Phần thưởng sẽ được gửi đến bạn trong thời gian
                                sớm nhất
                            </Typography>

                            <Button
                                variant="contained"
                                onClick={handleCloseResult}
                                sx={{
                                    borderRadius: 50,
                                    fontSize: 16,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                }}
                            >
                                Tuyệt vời! ✨
                            </Button>
                        </>
                    ) : (
                        <>
                            <Typography
                                sx={{
                                    fontSize: 24,
                                    fontWeight: 700,
                                    color: 'rgba(0,0,0,0.8)',
                                    mb: 3,
                                    mt: 2,
                                }}
                            >
                                {resultMessage || 'Chúc bạn may mắn lần sau!'}
                            </Typography>

                            <Button
                                variant="contained"
                                onClick={handleCloseResult}
                                sx={{
                                    borderRadius: 50,
                                    fontSize: 16,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                }}
                            >
                                Đóng
                            </Button>
                        </>
                    )}
                </PrizeContent>
            </PrizeDialog>
        </>
    );
};

export default WheelOfName;
